import React, { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  SettingOutlined,
  PrinterOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Tag from 'antd/lib/tag'
import Tabs from 'antd/lib/tabs'
import Badge from 'antd/lib/badge'
import Table from 'antd/lib/table'
import Button from 'antd/lib/button'
import Select from 'antd/lib/select'
import message from 'antd/lib/message'
import Tooltip from 'antd/lib/tooltip'
import Divider from 'antd/lib/divider'
import Checkbox from 'antd/lib/checkbox'
import capitalize from 'lodash/capitalize'
import styled from '@emotion/styled'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { useSession } from '@slerp/accounts'
import { GET_ADMIN_USER_EMAIL, GET_MERCHANT_STORE } from '../LocationQueries'
import {
  ORDER_STATS,
  GET_STORE_ORDERS,
  GET_MERCHANT_ORDER_STORES,
  GET_DATES_WITH_ORDERS_BY_MONTH
} from './DashboardQueries'
import { UPDATE_SETTINGS } from './../actions'
import Inventories from './Inventories'
import {
  FilterIcon,
  CalendarIcon,
  LocationIcon,
  SelectDownArrowIcon
} from '@slerp/assets'
import { SelectPrefixWrapper } from './../../Widgets/'
import OrderDrawer from '../../Orders/OrderDrawer'
import NotFound from '../../Misc/NotFound'
import { print } from '../../Orders/utils'
import { RangeValue } from 'rc-picker/lib/interface'
import DatePicker from './DatePicker'
import { createPortal } from 'react-dom'
import { ColumnType } from 'antd/lib/table'
import { Order } from '../../Orders/types'
import { TableCellNoWrap } from 'components/Common'
import FormattedDate from 'components/Utils/FormattedDate'
import { StatusIcon, StatusTag } from '../commonStyles'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'

interface StoreBasicInfo {
  id: string
  name: string
  slug: string
  isOpen: boolean
  settings: {
    prep_mode: string
  }
}

interface MerchantOrderStoresData {
  id: string
  slug: string
  name: string
}

const { TabPane } = Tabs
const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_ORDER_STATUSES = [
  'pending',
  'accepted',
  'fulfilled',
  'refunded',
  'cancelled',
  'rejected'
]
const ORDER_STATUSES_TAG_COLORS: { [key: string]: string } = {
  pending: 'orange',
  accepted: 'blue',
  fulfilled: 'green',
  refunded: 'purple',
  cancelled: 'red',
  rejected: 'magenta'
}

const PREP_TYPES = [
  {
    value: 'quiet',
    label: 'Quiet'
  },
  {
    value: 'moderate',
    label: 'Moderate'
  },
  {
    value: 'busy',
    label: 'Busy'
  }
]

const humanizePlatform = (str: string) => {
  return str === 'ios' ? 'iOS' : str?.charAt(0).toUpperCase() + str?.slice(1)
}

const LocationDashboard = () => {
  const { slug, tab } = useParams<{ slug: string; tab?: string }>()
  const { user, merchant } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const { merchantCategories: categories } = useMerchant()

  const [ordersToday, setOrdersToday] = useState<number>(0)
  const [productSales, setProductSales] = useState<number>(0)
  const [orderStatusFilter, setOrderStatusFilter] = useState<string[] | 'all'>(
    'all'
  )
  const [filterStartDate, setFilterStartDate] = useState<Dayjs>(
    dayjs()
  )
  const [filterEndDate, setFilterEndDate] = useState<Dayjs>(
    dayjs().add(1, 'days')
  )
  const [storeBasicInfo, setStoreBasicInfo] = useState<StoreBasicInfo>({
    id: '',
    name: '',
    slug: '',
    isOpen: false,
    settings: {
      prep_mode: ''
    }
  })
  const [mainTab, setMainTab] = useState('1')
  const [secondaryTab, setSecondaryTab] = useState('1')
  const [merchantOrderStores, setMerchantOrderStores] = useState<
    Array<MerchantOrderStoresData>
  >([])
  const [prepModeLoading, setPrepModeLoading] = useState(false)
  const [isInventoryDrawerOpen, setIsInventoryDrawerOpen] = useState(false)
  const [inventoryDrawerTab, setInventoryDrawerTab] = useState('products')

  const datesWithOrdersByMonth = useQuery(GET_DATES_WITH_ORDERS_BY_MONTH, {
    skip: true,
    fetchPolicy: 'no-cache'
  })
  const fetchDatesWithOrdersByMonth = datesWithOrdersByMonth.refetch
  const [datesWithOrders, setDatesWithOrders] = useState<{
    [key: string]: string[]
  }>({})

  const storeId = storeBasicInfo.id

  const [updateSettings] = useMutation(UPDATE_SETTINGS, {
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (!orderStatusFilter || orderStatusFilter.length === 0)
      setOrderStatusFilter('all')
  }, [orderStatusFilter])

  const [activeOrder, setActiveOrder] = useState<{
    transaction_id: string
  } | null>()
  const [drawerVisible, setDrawerVisible] = useState(false)

  const showDrawer = (order: Order) => {
    setDrawerVisible(true)
    setActiveOrder(order)
  }

  const onDrawerClose = () => {
    setDrawerVisible(false)
    setActiveOrder(null)
  }

  const ORDER_TABLE_COLUMNS = [
    {
      title: 'Order Id',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      render: (id: string) => {
        return <strong className='_cl-primary'>{id}</strong>
      }
    },
    {
      title: 'Type',
      dataIndex: 'fulfillment_type',
      key: 'fulfillment_type',
      render: (text: string) => {
        return <span className='_capitalize'>{text}</span>
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text = 'pending') => {
        return <Tag color={ORDER_STATUSES_TAG_COLORS[text]}>{text}</Tag>
      }
    },
    {
      title: <TableCellNoWrap>Subtotal</TableCellNoWrap>,
      dataIndex: 'subtotal',
      key: 'subtotal',
      render: (subtotal: number) => <span>£{subtotal.toFixed(2)}</span>
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => {
        return <span>£{total.toFixed(2)}</span>
      }
    },
    {
      title: <div className='_text-right'>Ordered At</div>,
      dataIndex: 'inserted_at',
      key: 'inserted_at',
      render: (orderedAt: Date) => {
        return (
          <TableCellNoWrap className='_text-right'>
            <FormattedDate>{orderedAt}</FormattedDate>
          </TableCellNoWrap>
        )
      }
    },
    {
      title: <div className='_text-right'>Fulfilment Date</div>,
      dataIndex: 'fulfillment_date',
      key: 'fulfillment_date',
      render: (fulfillmentDate: Date) => (
        <div className='_text-right'>
          {fulfillmentDate ? (
            <FormattedDate>{fulfillmentDate}</FormattedDate>
          ) : (
            'ASAP'
          )}
        </div>
      )
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string, order: Order) => (
        <span className='order-platform'>
          {humanizePlatform(order?.create_platform || '')} -{' '}
          {humanizePlatform(order?.create_device_category || '')}
        </span>
      )
    },
    {
      title: 'Print',
      dataIndex: 'transaction_id',
      align: 'right',
      width: 85,
      render: (transaction_id: string, order: Order) => {
        return (
          <Button
            icon={<PrinterOutlined className='_no-pointer-events' />}
            role='print'
            onClick={() => print(transaction_id)}
          />
        )
      }
    }
  ]

  const handlePrepModeChange = (mode: string) => {
    setPrepModeLoading(true)
    updateSettings({
      variables: {
        merchant_id: merchant?.id,
        id: storeBasicInfo.id,
        settings: { prep_mode: mode }
      }
    }).then(() => {
      refetch()
      setPrepModeLoading(false)
    })
  }

  const orderDateOnchangeHandler = (dateRange: RangeValue<Dayjs>) => {
    const start = (dateRange && dateRange[0]) || dayjs()
    let end = (dateRange && dateRange[1]) || dayjs()
    if (start.isSame(end, 'date')) {
      end = end.add(1, 'day')
    }
    setFilterStartDate(start)
    setFilterEndDate(end)
  }

  const { refetch: orderStatsRefetch } = useQuery(ORDER_STATS, {
    variables: {
      merchantId: merchant?.id || '',
      storeId: storeBasicInfo.id,
      startDate: filterStartDate.format(DATE_FORMAT),
      endDate: filterEndDate.format(DATE_FORMAT),
      statuses:
        orderStatusFilter === 'all' ? DEFAULT_ORDER_STATUSES : orderStatusFilter
    },
    skip: !storeBasicInfo.id,
    onCompleted: (data) => {
      const { orderStats } = data
      const { count = 0, gmv = 0 } = orderStats || {}
      setProductSales(gmv.toFixed(2))
      setOrdersToday(count)
    },
    onError: (error) => {
      message.destroy()
      message.error(`Unable to get order stats. Due to ${error.message}`, 3)
    }
  })

  const { data: ordersData, refetch: ordersDataRefetch } = useQuery(
    GET_STORE_ORDERS,
    {
      variables: {
        storeId: storeBasicInfo.id,
        statuses:
          orderStatusFilter === 'all'
            ? DEFAULT_ORDER_STATUSES
            : orderStatusFilter,
        startDate: filterStartDate.format(DATE_FORMAT),
        endDate: filterEndDate.format(DATE_FORMAT)
      },
      skip: !storeBasicInfo.id,
      onCompleted: () => {
        orderStatsRefetch()
      }
    }
  )

  const orders = ordersData && ordersData?.orders.map((order: Order) => order)

  const isSuperAdmin = user?.role === 'superadmin'

  const { data: adminUserData, loading: isLoadingAdminUserData } = useQuery<{
    users: Array<{ email: string }>
  }>(GET_ADMIN_USER_EMAIL, {
    variables: {
      merchant_id: merchant?.id
    },
    skip: !isSuperAdmin
  })

  const {
    data: storeData,
    loading: storeDataLoading,
    refetch
  } = useQuery(GET_MERCHANT_STORE, {
    variables: {
      merchant_id: merchant?.id,
      store_slug: slug,
      email: isSuperAdmin ? adminUserData?.users[0]?.email : user?.email
    },
    fetchPolicy: 'no-cache',
    skip: isSuperAdmin && isLoadingAdminUserData && !adminUserData
  })

  const { data: merchantOrderStoresData } = useQuery(
    GET_MERCHANT_ORDER_STORES,
    {
      variables: {
        merchantId: merchant?.id,
        userEmail: user?.email
      }
    }
  )

  useEffect(() => {
    if (location.pathname.includes('/inventory/same-day')) {
      setMainTab('2')
    }

    if (location.pathname.includes('/inventory/pre-orders')) {
      setMainTab('2')
      setSecondaryTab('2')
    }

    if (location.pathname.includes('/edit/')) {
      setIsInventoryDrawerOpen(true)
      if (tab === 'products') {
        setInventoryDrawerTab('products')
      } else if (tab === 'modifiers') {
        setInventoryDrawerTab('modifiers')
      }
    } else {
      setIsInventoryDrawerOpen(false)
    }
  }, [location.pathname, tab])

  useEffect(() => {
    if (storeData && storeData.stores?.length) {
      const { id, name, slug, is_open, settings } = storeData.stores[0]
      setStoreBasicInfo({ id, name, slug, isOpen: is_open, settings })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeData])

  useEffect(() => {
    if (storeBasicInfo.id) {
      ordersDataRefetch({
        storeId: storeBasicInfo.id,
        statuses:
          orderStatusFilter === 'all'
            ? DEFAULT_ORDER_STATUSES
            : orderStatusFilter,
        startDate: filterStartDate.format(DATE_FORMAT),
        endDate: filterEndDate.format(DATE_FORMAT)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStartDate, filterEndDate, orderStatusFilter, storeBasicInfo])

  useEffect(() => {
    if (merchantOrderStoresData) {
      const { stores } = merchantOrderStoresData
      setMerchantOrderStores(stores)
    }
  }, [merchantOrderStoresData])

  const moveToStore = (storeSlug: string) => {
    window.location.pathname = `/location/${storeSlug}/orders`
  }

  if (!storeDataLoading && storeData && !storeData.stores?.length) {
    return <NotFound />
  }

  const portalTarget = document.getElementById('layout-actions')

  return (
    <>
      {portalTarget &&
        createPortal(
          <Row justify='end' align='middle' gutter={12} className='_mb-0'>
            <Col>
              <Button
                className='_ht-36'
                onClick={() => navigate(`/location/${slug}/availability-rules`)}
                data-testid='inventory-dashboard-special-availability-btn'
              >
                Availability Rules
              </Button>
            </Col>
            <Col>
              <Tooltip
                title={
                  <span>
                    Manage your store's opening hours, users and order types
                    here.
                  </span>
                }
              >
                <Button
                  icon={
                    <SettingOutlined style={{ verticalAlign: '0.125em' }} />
                  }
                  onClick={() => navigate(`/location/${slug}/settings/general`)}
                  className='_center-vertical _uppercase _ht-36'
                >
                  Settings
                </Button>
              </Tooltip>
            </Col>
          </Row>,
          portalTarget
        )}
      <Row justify='space-between' align='middle' className='_mb-8'>
        <Row gutter={24} align='middle'>
          <Col>
            <Tooltip
              title={
                <span>
                  Your{' '}
                  <a
                    href='https://support.slerp.com/knowledge/changing-your-preparation-time'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    activity mode
                  </a>{' '}
                  determines your prep time for each order and is reflected on
                  your tablet.
                </span>
              }
            >
              <Select
                className='stats-component__select'
                onChange={handlePrepModeChange}
                value={storeBasicInfo?.settings?.prep_mode}
                loading={prepModeLoading}
              >
                {PREP_TYPES.map((type: { value: string; label: string }) => (
                  <Select.Option key={type.value} value={type.value}>
                    {type.label}
                  </Select.Option>
                ))}
              </Select>
            </Tooltip>
          </Col>
          {merchantOrderStores.length > 1 && (
            <Col>
              {storeBasicInfo.id && merchantOrderStores && (
                <SelectPrefixWrapper prefixicon={<LocationIcon />}>
                  <Select
                    suffixIcon={<SelectDownArrowIcon />}
                    defaultValue={storeBasicInfo.slug}
                    onChange={moveToStore}
                    style={{
                      minWidth: 'max-content'
                    }}
                    dropdownStyle={{
                      minWidth: '200px'
                    }}
                  >
                    {merchantOrderStores.map((store) => (
                      <Select.Option key={store.id} value={store.slug}>
                        {store.name}
                      </Select.Option>
                    ))}
                  </Select>
                </SelectPrefixWrapper>
              )}
            </Col>
          )}
          <Col>
            <StatusTag enabled={storeBasicInfo.isOpen}>
              <StatusIcon enabled={storeBasicInfo.isOpen} />
              <span>{storeBasicInfo.isOpen ? 'Open' : 'Closed'}</span>
            </StatusTag>
          </Col>
        </Row>
      </Row>
      <Row>
        <Col span={24}>
          <Tabs
            type='card'
            activeKey={mainTab}
            onChange={(key) => {
              setMainTab(key)
              if (key === '1') {
                navigate(`/location/${slug}/orders`)
              } else if (key === '2') {
                navigate(`/location/${slug}/inventory/same-day`)
              }
            }}
          >
            <TabPane
              tab={<a data-testid='location-orders-tab-pane'>ORDERS</a>}
              key='1'
            >
              <>
                <Row align='middle' gutter={[8, 8]} justify='space-between'>
                  <Col span={12}>
                    <Row justify='start' align='middle' gutter={40}>
                      <InsightsCol>
                        <Badge
                          color='#d092dd'
                          status='processing'
                          text={<BadgeText>{ordersToday}</BadgeText>}
                        />
                        <span className='badge -padding -margin'>Orders</span>
                      </InsightsCol>
                      <Col>
                        <Badge
                          color='purple'
                          status='processing'
                          text={
                            <b className='badge-text -xxxl'>{`£${productSales}`}</b>
                          }
                        />
                        <span className='badge -padding'>Product Sales</span>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row justify='end' align='middle'>
                      <SelectPrefixWrapper prefixicon={<CalendarIcon />}>
                        <DatePicker
                          {...{
                            datesWithOrders,
                            fetchDatesWithOrdersByMonth,
                            merchantId: merchant?.id || '',
                            storeId,
                            setDatesWithOrders,
                            orderDateOnchangeHandler
                          }}
                        />
                      </SelectPrefixWrapper>
                      <SelectPrefixWrapper
                        prefixicon={<FilterIcon />}
                        style={{ marginLeft: 10 }}
                      >
                        <Select
                          data-testid='order-status-filter-dropdown'
                          placeholder='Filter by Status'
                          onChange={(options: any) => {
                            const previous = orderStatusFilter
                            const upcoming = options || []
                            if (
                              !options ||
                              options.length === 0 ||
                              (!previous.includes('all') &&
                                upcoming.includes('all')) ||
                              DEFAULT_ORDER_STATUSES.length === upcoming.length
                            )
                              return setOrderStatusFilter('all')
                            return setOrderStatusFilter(
                              upcoming.filter(
                                (status: string) => status !== 'all'
                              )
                            )
                          }}
                          showSearch={false}
                          value={
                            orderStatusFilter === 'all'
                              ? [orderStatusFilter]
                              : orderStatusFilter
                          }
                          tagRender={({ value }) =>
                            value !== 'all' ? (
                              <span className='_capitalize'>{value}</span>
                            ) : (
                              <>All orders</>
                            )
                          }
                          maxTagCount='responsive'
                          className='control _w-16 -with-icon order-select'
                          mode='tags'
                          dropdownRender={(menu) => (
                            <div className='_py-4 _px-8'>{menu}</div>
                          )}
                          maxTagPlaceholder={() => (
                            <>
                              {orderStatusFilter === 'all'
                                ? 'All orders'
                                : `${orderStatusFilter.length} selected`}
                            </>
                          )}
                          listHeight={340}
                        >
                          <Select.Option
                            value='all'
                            data-testid='status-option-all'
                          >
                            <span>All orders</span>{' '}
                          </Select.Option>
                          <Select.Option disabled className='divider'>
                            <Divider className='_my-8' />
                          </Select.Option>
                          {DEFAULT_ORDER_STATUSES.map((state) => (
                            <Select.Option
                              value={state}
                              key={state}
                              className='_px-8 _my-2'
                              data-testid={`status-option-${state}`}
                            >
                              <Checkbox
                                checked={orderStatusFilter.includes(state)}
                                className='_mr-12'
                              />
                              {capitalize(state)}
                            </Select.Option>
                          ))}
                        </Select>
                      </SelectPrefixWrapper>
                    </Row>
                  </Col>
                </Row>
                <Row justify='center'>
                  <Col span={24}>
                    <Table
                      onRow={(record: Order) => ({
                        onClick: (e) => {
                          const el = e.target as HTMLElement
                          const role =
                            el && el.getAttribute && el.getAttribute('role')
                          if (role !== 'print') showDrawer(record)
                        }
                      })}
                      columns={ORDER_TABLE_COLUMNS as ColumnType<Order>[]}
                      dataSource={orders}
                      pagination={{
                        defaultPageSize: 100,
                        showSizeChanger: true
                      }}
                      rowClassName='_pointer'
                    />
                  </Col>
                </Row>
                {activeOrder && (
                  <OrderDrawer
                    transactionId={activeOrder.transaction_id}
                    visible={drawerVisible}
                    onClose={onDrawerClose}
                    ordersRefetch={refetch}
                    ordersDataRefetch={ordersDataRefetch}
                  />
                )}
              </>
            </TabPane>
            <TabPane
              tab={
                <Tooltip
                  title={
                    <span>
                      This is where you manage your store's product stock
                      levels. Learn more about inventories{' '}
                      <a
                        href='https://support.slerp.com/knowledge/understanding-inventories'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        here
                      </a>
                      .
                    </span>
                  }
                >
                  <a data-testid='location-inventory-tab-pane'>INVENTORY</a>
                </Tooltip>
              }
              key='2'
            >
              <Col className='_mt-16'>
                <Tabs
                  destroyInactiveTabPane
                  activeKey={secondaryTab}
                  onChange={(key) => {
                    setSecondaryTab(key)
                    if (key === '1') {
                      navigate(`/location/${slug}/inventory/same-day`)
                    } else {
                      navigate(`/location/${slug}/inventory/pre-orders`)
                    }
                  }}
                >
                  <Tabs.TabPane
                    tab={
                      <>
                        Same Day
                        <Tooltip
                          title={
                            <a
                              href='https://support.slerp.com/knowledge/understanding-inventories'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Same day inventory
                            </a>
                          }
                        >
                          <QuestionCircleOutlined className='_ml-8' />
                        </Tooltip>
                      </>
                    }
                    key='1'
                  >
                    <Col className='_mt-24'>
                      <Inventories
                        storeInfo={storeBasicInfo}
                        categories={categories}
                        categoryIds={categories?.map((category) => ({
                          category_id: { _eq: category.id }
                        }))}
                        isInventoryDrawerOpen={isInventoryDrawerOpen}
                        setIsInventoryDrawerOpen={setIsInventoryDrawerOpen}
                        inventoryDrawerTab={inventoryDrawerTab}
                        setInventoryDrawerTab={setInventoryDrawerTab}
                        navigate={navigate}
                        slug={slug}
                      />
                    </Col>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab={
                      <>
                        Pre-Orders
                        <Tooltip
                          title={
                            <a
                              href='https://support.slerp.com/knowledge/pre-order-inventory'
                              target='_blank'
                              rel='noreferrer'
                            >
                              Pre-orders Inventory
                            </a>
                          }
                        >
                          <QuestionCircleOutlined className='_ml-8' />
                        </Tooltip>
                      </>
                    }
                    key='2'
                  >
                    <Col className='_mt-24'>
                      <Inventories
                        storeInfo={storeBasicInfo}
                        categories={categories}
                        categoryIds={categories?.map((category) => ({
                          category_id: { _eq: category.id }
                        }))}
                        isPreorder
                        isInventoryDrawerOpen={isInventoryDrawerOpen}
                        setIsInventoryDrawerOpen={setIsInventoryDrawerOpen}
                        inventoryDrawerTab={inventoryDrawerTab}
                        setInventoryDrawerTab={setInventoryDrawerTab}
                        navigate={navigate}
                        slug={slug}
                      />
                    </Col>
                  </Tabs.TabPane>
                </Tabs>
              </Col>
            </TabPane>
          </Tabs>
        </Col>
      </Row>
    </>
  )
}

const InsightsCol = styled(Col)`
  margin-right: 44px;
`

const BadgeText = styled.b`
  font-size: 20px;
  font-weight: 500;
  line-height: 1;
  color: #4a4a68;
`

export default LocationDashboard
